<template>
  <!-- Floating Chat Icon (closed state) — always in DOM, just hidden when chat is open -->
  <button
    v-show="!isOpen"
    class="chat-fab"
    @click="openChat"
    title="Open FormIQ Chat Assist"
  >
    <span class="chat-fab__icon">💬</span>
    <span class="chat-fab__label">Chat Assist</span>
  </button>

  <!-- Chat Window — always mounted so deep-chat keeps its conversation history -->
  <div
    v-show="isOpen"
    class="chat-window"
    :style="windowStyle"
    ref="windowRef"
  >
    <!-- Header (drag handle) -->
    <div
      class="chat-window__header"
      @mousedown.prevent="startDrag"
      @touchstart.prevent="startDragTouch"
    >
      <div class="header__left">
        <svg class="header__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8 10h8M8 13h5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <div class="header__titles">
          <span class="header__name">FormIQ Chat Assist</span>
          <span class="header__status" v-if="showIntro">● Sprache wählen</span>
          <span class="header__status" v-else>{{ activeLangLabel }}</span>
        </div>
      </div>
      <div class="header__controls" @mousedown.stop>
        <!-- Language switcher: only after conversation started -->
        <div v-if="!showIntro" class="lang-switcher">
          <button
            v-for="lang in languages"
            :key="lang.code"
            class="lang-btn"
            :class="{ 'lang-btn--active': currentLanguage === lang.code }"
            @click.stop="switchLanguage(lang.code)"
            :title="lang.label"
          >{{ lang.flag }}</button>
        </div>
        <button class="close-btn" @click.stop="closeChat" title="Close">✕</button>
      </div>
    </div>

    <!-- Intro panel (shown once per session until dismissed) -->
    <div v-if="showIntro" class="chat-intro">
      <div class="intro__icon">🤖</div>
      <h3 class="intro__title">Hallo, ich bin FormIQ.</h3>
      <p class="intro__desc">Ich begleite Sie durch Ihren Antrag — beantworte Fragen, erkläre Felder und helfe Ihnen, alles richtig auszufüllen.</p>
      <p class="intro__hint">In welcher Sprache möchten Sie starten?</p>
      <div class="intro__lang-btns">
        <button
          v-for="lang in languages"
          :key="lang.code"
          class="intro__lang-btn"
          :style="{ borderColor: lang.color }"
          @click="selectIntroLanguage(lang.code)"
        >{{ lang.flag }} {{ lang.label }}</button>
      </div>
    </div>

    <!-- Drag shield: transparent overlay prevents deep-chat from stealing mouse events during drag/resize -->
    <div v-if="isDraggingActive || isResizingActive" class="drag-shield" />

    <!-- Deep Chat (hidden while intro shown) -->
    <div class="chat-window__body" :style="{ display: showIntro ? 'none' : 'flex' }">
      <deep-chat
        ref="deepChatRef"
        style="width:100%;height:100%;border:none;border-radius:0;"
        :connect="connectConfig"
      />
    </div>

    <!-- Resize handle -->
    <div
      class="resize-handle"
      @mousedown.stop="startResize"
      @touchstart.prevent.stop="startResizeTouch"
      title="Drag to resize"
    >⤡</div>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue';
import config from '../config.js';
import { authFetch } from '../utils/apiClient.js';

export default {
  name: 'ChatWindow',
  props: {
    formId:    { type: String,  required: true },
    clientId:  { type: String,  default: null },
    uiPayload: { type: Object,  default: null },
  },

  setup(props) {
    const API_KEY = 'HFOKEk5cLY1HFahMm6VGya04Rq531tWZqLREaPxc';

    const isOpen = ref(false);
    const showIntro = ref(true);
    const deepChatRef = ref(null);
    const windowRef = ref(null);

    // Window position & size
    const pos = reactive({ x: window.innerWidth - 440, y: window.innerHeight - 560 });
    const size = reactive({ w: 420, h: 520 });

    const windowStyle = computed(() => ({
      left: `${Math.max(0, Math.min(pos.x, window.innerWidth - size.w))}px`,
      top: `${Math.max(0, Math.min(pos.y, window.innerHeight - size.h))}px`,
      width: `${Math.max(320, size.w)}px`,
      height: `${Math.max(400, size.h)}px`,
    }));

    // Language
    const languages = [
      { code: 'German',  label: 'Auf Deutsch starten',  continueLabel: '● Weiter auf Deutsch', flag: '🇩🇪', color: '#003366' },
      { code: 'English', label: 'Start in English',     continueLabel: '● Continue in English', flag: '🇬🇧', color: '#22c55e' },
      { code: 'Turkish', label: 'Türkçe başlat',        continueLabel: '● Türkçe devam',        flag: '🇹🇷', color: '#f59e0b' },
    ];
    const currentLanguage = ref('English');
    const activeLangLabel = computed(() =>
      languages.find(l => l.code === currentLanguage.value)?.continueLabel || '● Online'
    );

    // Chat state
    let chatHistory = [];

    // ---- Deep Chat connect handler ----
    const handler = (body, signals) => {
      const userMsg = body.messages[0].text;

      const isLocal = config.env === 'local';
      const url = `${config.backendUrl}/v1/query/${props.clientId || props.formId}/user_query`;

      const headers = { 'Content-Type': 'application/json' };
      if (!isLocal) headers['x-api-key'] = API_KEY;

      const requestBody = {
        language: currentLanguage.value,
        user_query: userMsg,
        chat_history: chatHistory,
      };
      if (props.uiPayload) {
        requestBody.ui_payload = props.uiPayload;
      }

      authFetch(url, { method: 'POST', headers, body: JSON.stringify(requestBody) })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const text = await res.json();
          chatHistory.push({ user_message: userMsg, system_message: text });
          signals.onResponse({ text });
        })
        .catch(() => {
          signals.onResponse({ text: 'Sorry, I could not reach the server. Please try again.' });
        });
    };

    const connectConfig = ref({ handler });

    // ---- Language switching ----
    const switchLanguage = (lang) => {
      currentLanguage.value = lang;
    };

    const selectIntroLanguage = (lang) => {
      currentLanguage.value = lang;
      showIntro.value = false;
      // Inject the full FormIQ intro as the first chat message
      setTimeout(() => {
        if (deepChatRef.value) {
          const introMessages = {
            German: `Hallo, ich bin **FormIQ**. Ich helfe Ihnen dabei, Ihren Antrag Schritt für Schritt auszufüllen.\n\n**Was ich kann:**\n- Ich erkläre Ihnen, was in den einzelnen Feldern des Antrags gemeint ist.\n- Ich beantworte Ihre Fragen in klarer und einfacher Sprache.\n- Ich helfe Ihnen, Fehler zu vermeiden und wichtige Angaben nicht zu vergessen.\n\n**Wie ich Sie unterstütze:**\n- Während Sie den Antrag ausfüllen, können Sie mich jederzeit etwas fragen.\n- Ich gebe Hinweise, wenn Informationen fehlen oder unklar sind.\n- Ich begleite Sie durch den gesamten Antrag, bis alles vollständig ist.\n\n**Wie Sie mich nutzen:**\nSchreiben Sie mir einfach Ihre Frage oder sagen Sie, wobei Sie Hilfe brauchen. Ich antworte sofort und führe Sie Schritt für Schritt weiter.`,
            English: `Hello, I'm **FormIQ**. I'm here to help you complete your application step by step.\n\n**What I can do:**\n- I explain what each field in the application means.\n- I answer your questions in clear and simple language.\n- I help you avoid mistakes and ensure no important details are missed.\n\n**How I support you:**\n- You can ask me anything at any point while filling out the form.\n- I provide hints when information is missing or unclear.\n- I guide you through the entire application until everything is complete.\n\n**How to use me:**\nSimply write your question or tell me what you need help with. I'll respond immediately and guide you step by step.`,
            Turkish: `Merhaba, ben **FormIQ**. Başvurunuzu adım adım doldurmanıza yardımcı olmak için buradayım.\n\n**Neler yapabilirim:**\n- Başvurudaki her alanın ne anlama geldiğini açıklarım.\n- Sorularınızı açık ve sade bir dilde yanıtlarım.\n- Hata yapmamanıza ve önemli bilgileri atlamanıza engel olurum.\n\n**Nasıl destek veririm:**\n- Formu doldururken istediğiniz zaman soru sorabilirsiniz.\n- Bilgi eksik veya belirsiz olduğunda ipuçları veririm.\n- Başvuru tamamlanana kadar tüm süreç boyunca yanınızdayım.\n\n**Nasıl kullanırsınız:**\nSorunuzu yazın ya da yardım istediğiniz konuyu belirtin. Hemen yanıt veririm ve sizi adım adım yönlendiririm.`,
          };
          deepChatRef.value.addMessage?.({ role: 'ai', text: introMessages[lang] });
        }
      }, 100);
    };

    // ---- Open / Close ----
    const openChat = () => {
      isOpen.value = true;
      pos.x = window.innerWidth - size.w - 20;
      pos.y = window.innerHeight - size.h - 20;
    };
    const closeChat = () => { isOpen.value = false; };

    // ---- Drag logic ----
    const dragState = reactive({ active: false, ox: 0, oy: 0 });
    const resizeState = reactive({ active: false, ox: 0, oy: 0, ow: 0, oh: 0 });

    const isDraggingActive = computed(() => dragState.active);
    const isResizingActive = computed(() => resizeState.active);

    const startDrag = (e) => {
      dragState.active = true;
      dragState.ox = e.clientX - pos.x;
      dragState.oy = e.clientY - pos.y;
    };

    const startDragTouch = (e) => {
      dragState.active = true;
      dragState.ox = e.touches[0].clientX - pos.x;
      dragState.oy = e.touches[0].clientY - pos.y;
    };

    const onMouseMove = (e) => {
      if (!dragState.active) return;
      pos.x = e.clientX - dragState.ox;
      pos.y = e.clientY - dragState.oy;
    };

    const onTouchMove = (e) => {
      if (!dragState.active) return;
      pos.x = e.touches[0].clientX - dragState.ox;
      pos.y = e.touches[0].clientY - dragState.oy;
    };

    const stopDrag = () => { dragState.active = false; };

    // ---- Resize logic ----
    const startResize = (e) => {
      resizeState.active = true;
      resizeState.ox = e.clientX;
      resizeState.oy = e.clientY;
      resizeState.ow = size.w;
      resizeState.oh = size.h;
    };

    const startResizeTouch = (e) => {
      resizeState.active = true;
      resizeState.ox = e.touches[0].clientX;
      resizeState.oy = e.touches[0].clientY;
      resizeState.ow = size.w;
      resizeState.oh = size.h;
    };

    const onResizeMove = (e) => {
      if (!resizeState.active) return;
      size.w = Math.max(320, resizeState.ow + (e.clientX - resizeState.ox));
      size.h = Math.max(400, resizeState.oh + (e.clientY - resizeState.oy));
    };

    const onResizeTouchMove = (e) => {
      if (!resizeState.active) return;
      size.w = Math.max(320, resizeState.ow + (e.touches[0].clientX - resizeState.ox));
      size.h = Math.max(400, resizeState.oh + (e.touches[0].clientY - resizeState.oy));
    };

    const stopResize = () => { resizeState.active = false; };

    // ---- Global event listeners ----
    const onGlobalMouseMove = (e) => { onMouseMove(e); onResizeMove(e); };
    const onGlobalTouchMove = (e) => { onTouchMove(e); onResizeTouchMove(e); };
    const onGlobalUp = () => { stopDrag(); stopResize(); };

    // ---- Handle "Frag FormIQ" popover events ----
    const handleAskAbout = (e) => {
      const question = e.detail?.text;
      if (!question) return;

      // Open the chat window if closed
      if (!isOpen.value) isOpen.value = true;

      // If intro is still showing, pick German as default and dismiss
      const wasIntro = showIntro.value;
      if (wasIntro) {
        selectIntroLanguage('German');
      }

      // Submit after deep-chat is ready (longer delay if intro was showing)
      setTimeout(() => {
        if (deepChatRef.value) {
          deepChatRef.value.submitUserMessage?.({ text: question });
        }
      }, wasIntro ? 500 : 150);
    };

    onMounted(() => {
      window.addEventListener('mousemove', onGlobalMouseMove);
      window.addEventListener('mouseup', onGlobalUp);
      window.addEventListener('touchmove', onGlobalTouchMove);
      window.addEventListener('touchend', onGlobalUp);
      window.addEventListener('formpilot:ask-about', handleAskAbout);
    });

    onUnmounted(() => {
      window.removeEventListener('mousemove', onGlobalMouseMove);
      window.removeEventListener('mouseup', onGlobalUp);
      window.removeEventListener('touchmove', onGlobalTouchMove);
      window.removeEventListener('touchend', onGlobalUp);
      window.removeEventListener('formpilot:ask-about', handleAskAbout);
    });

    return {
      isOpen, showIntro, activeLangLabel, deepChatRef, windowRef,
      windowStyle, languages, currentLanguage,
      connectConfig,
      isDraggingActive, isResizingActive,
      openChat, closeChat,
      switchLanguage, selectIntroLanguage,
      startDrag, startDragTouch,
      startResize, startResizeTouch,
    };
  },
};
</script>

<style scoped>
/* ---- FAB ---- */
.chat-fab {
  position: fixed;
  bottom: 90px;
  right: 28px;
  z-index: 1000;
  background: linear-gradient(135deg, #1e293b, #334155);
  color: #fff;
  border: none;
  border-radius: 50px;
  padding: 14px 22px 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.35);
  transition: transform 0.2s, box-shadow 0.2s;
  font-size: 15px;
  font-weight: 700;
}
.chat-fab:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.5);
}
.chat-fab__icon { font-size: 22px; }

/* ---- Window ---- */
.chat-window {
  position: fixed;
  z-index: 1000;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 16px 56px rgba(0,0,0,0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.08);
  user-select: none;
}

/* ---- Header ---- */
.chat-window__header {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: grab;
  flex-shrink: 0;
  gap: 8px;
}
.chat-window__header:active { cursor: grabbing; }

/* Transparent shield prevents deep-chat/iframes from swallowing mouse events during drag/resize */
.drag-shield {
  position: absolute;
  inset: 0;
  z-index: 10;
  cursor: grabbing;
}

.header__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.header__icon { width: 22px; height: 22px; flex-shrink: 0; }

.header__titles {
  display: flex;
  flex-direction: column;
}
.header__name {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}
.header__status {
  font-size: 10px;
  color: #86efac;
  font-weight: 600;
}

.header__controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.lang-switcher {
  display: flex;
  gap: 4px;
}

.lang-btn {
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.75);
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.lang-btn:hover {
  background: rgba(255,255,255,0.22);
  color: #fff;
}
.lang-btn--active {
  background: #334155;
  border-color: #334155;
  color: #fff;
}

.close-btn {
  background: rgba(255,255,255,0.1);
  border: none;
  color: rgba(255,255,255,0.7);
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  flex-shrink: 0;
}
.close-btn:hover { background: rgba(239,68,68,0.4); color: #fff; }

/* ---- Intro ---- */
.chat-intro {
  padding: 28px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  background: #fafafa;
  flex: 1;
}

.intro__icon { font-size: 36px; }

.intro__title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a2e;
}

.intro__desc {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.intro__block {
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
}

.intro__block-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #c0121c;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
}

.intro__list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.intro__list li {
  font-size: 12px;
  color: #374151;
  padding-left: 14px;
  position: relative;
  line-height: 1.45;
}

.intro__list li::before {
  content: '▸';
  color: #003366;
  font-size: 10px;
  position: absolute;
  left: 0;
  top: 1px;
}

.intro__hint {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  margin-top: 4px;
}

.intro__lang-btns {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.intro__lang-btn {
  padding: 11px 16px;
  border-radius: 6px;
  border: 2px solid;
  background: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  color: #1a1a2e;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}
.intro__lang-btn:hover {
  background: #f3f4f6;
  transform: translateY(-1px);
}

/* ---- Chat Body ---- */
.chat-window__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ---- Resize handle ---- */
.resize-handle {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 24px;
  height: 24px;
  cursor: se-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
  border-top-left-radius: 8px;
  user-select: none;
}
</style>
